/**
 * APIconButton Component
 *
 * A comprehensive icon button component following Figma design system specifications.
 * Supports multiple sizes, variants, states, and accessibility features.
 * Designed specifically for icon-only buttons without text labels.
 *
 * @example
 * ```tsx
 * <APIconButton
 *   size="large"
 *   variant="primary"
 *   icon="home"
 *   aria-label="Go to home"
 *   onClick={handleClick}
 * />
 *
 * <APIconButton
 *   size="medium"
 *   variant="secondary"
 *   icon="delete"
 *   aria-label="Delete item"
 *   loading={isLoading}
 * />
 * ```
 */

import { forwardRef } from 'react';

import type { IAPIconButtonProps } from './types';

import './styles.scss';

import APIconAstropay from '../media/icon-ap';
import APSpinner from '../spinner';

/**
 * APIconButton - Production-ready icon button component
 *
 * Follows AstroPay design system and accessibility guidelines.
 * Based on Figma Icon Button component specifications.
 *
 */
export const APIconButton = forwardRef<HTMLButtonElement, IAPIconButtonProps>(
  (
    {
      size = 'medium',
      variant = 'primary',
      icon,
      loading = false,
      disabled = false,
      className = '',
      type = 'button',
      'data-testid': testId,
      'aria-label': ariaLabel,
      ...rest
    },
    ref
  ) => {
    // Determine if button should be disabled
    const isDisabled = disabled || loading;

    // Map to panel.css .ap-btn .ap-btn--icon pattern
    const VARIANT_MAP: Record<string, string> = {
      primary:     'ap-btn--primary',
      secondary:   'ap-btn--secondary',
      tertiary:    'ap-btn--ghost',
      destructive: 'ap-btn--danger',
      transparent: 'ap-btn--ghost',
      'transparent-black': 'ap-btn--ghost',
      text:        'ap-btn--link',
      danger:      'ap-btn--danger',
      ghost:       'ap-btn--ghost',
    };

    // Map sizes
    const SIZE_MAP: Record<string, string | null> = {
      small:  'ap-btn--sm',
      medium: null,
      large:  'ap-btn--lg',
    };

    const variantClass = VARIANT_MAP[variant] ?? `ap-btn--${variant}`;
    const sizeClass    = SIZE_MAP[size] ?? null;

    const stateClasses = [
      loading && 'is-loading',
      isDisabled && !loading && 'ap-btn--disabled',
    ].filter(Boolean);

    // Use ap-btn ap-btn--icon pattern from panel.css
    const buttonClasses = ['ap-btn', 'ap-btn--icon', variantClass, sizeClass, ...stateClasses, className]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={buttonClasses}
        data-testid={testId}
        aria-label={loading ? `Loading, ${ariaLabel}` : ariaLabel}
        {...rest}
      >
        {/* Loading spinner */}
        {loading ? (
          <APSpinner size={'small'} variant={'primary'} className="ap-btn__spinner" />
        ) : (
          /* Icon */
          <span className="ap-btn__icon">
            <APIconAstropay icon={icon} size={size} />
          </span>
        )}
      </button>
    );
  }
);

APIconButton.displayName = 'APIconButton';

export type { IAPIconButtonProps };
export default APIconButton;
